import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingBag, Heart, Share2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../App';
import { toast } from 'sonner';
import { AfterpayBadge } from '../components/AfterpayBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('notes');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { addToCart, loading: cartLoading } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    if (!product) return;
    if (!product.in_stock) { toast.error('This item is sold out'); return; }
    await addToCart(product.id, quantity);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-32 flex items-center justify-center">
        <div className="w-16 h-16 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#050505] pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white mb-4">Product Not Found</h1>
          <Link to="/products" className="text-[#D4AF37]">← Back to Products</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button onClick={() => navigate(-1)} data-testid="back-to-collection-btn" className="inline-flex items-center gap-2 text-[#D4AF37] mb-8 hover:text-[#E5C158] transition-colors">
          <ArrowLeft size={18} />
          <span className="font-heading text-xs tracking-[0.2em]">BACK TO COLLECTION</span>
        </button>

        <div className="grid lg:grid-cols-2 gap-12 md:gap-16">
          {/* Product Image Gallery */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative aspect-square border border-[#D4AF37]/20 bg-[#0a0a0a] overflow-hidden">
              {(() => {
                const allImages = [product.image, ...(product.gallery || [])].filter(Boolean);
                const currentImage = allImages[activeImageIndex] || product.image;
                return currentImage ? (
                  <img
                    src={currentImage}
                    alt={product.name}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    data-testid="product-detail-image"
                    key={activeImageIndex}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center" data-testid="product-detail-image">
                    <span className="font-heading text-5xl mb-3 text-[#D4AF37]/20">&#10022;</span>
                    <span className="font-heading text-sm tracking-[0.2em] text-[#D4AF37]/30">IMAGE COMING SOON</span>
                  </div>
                );
              })()}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/30 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-[#D4AF37]/40 hidden md:block" />
            <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-[#D4AF37]/40 hidden md:block" />

            {/* Thumbnail Gallery */}
            {product.gallery && product.gallery.length > 0 && (
              <div className="flex gap-2 mt-4 overflow-x-auto hide-scrollbar" data-testid="product-gallery-thumbnails">
                {[product.image, ...product.gallery].filter(Boolean).map((img, idx) => (
                  <button
                    key={img}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 border overflow-hidden transition-all duration-200 ${
                      activeImageIndex === idx
                        ? 'border-[#D4AF37] opacity-100'
                        : 'border-[#D4AF37]/15 opacity-50 hover:opacity-80'
                    }`}
                    data-testid={`gallery-thumb-${idx}`}
                  >
                    <img src={img} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <p className="font-arabic text-xl text-[#D4AF37]/60 mb-2">عطر فاخر</p>
            <p className="font-heading text-xs tracking-[0.3em] text-[#D4AF37] mb-2" data-testid="product-category">
              {product.brand?.toUpperCase()}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl tracking-wide text-white mb-2" data-testid="product-name">
              {product.name}
            </h1>
            <p className="font-body text-white/60 text-lg mb-6" data-testid="product-subtitle">
              {product.subtitle}
            </p>

            {/* Feels Like */}
            {product.scent_profile?.feels_like && (
              <div className="mb-8 p-6 border border-[#D4AF37]/20 bg-[#0a0a0a]/50">
                <p className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2">FEELS LIKE</p>
                <p className="font-accent italic text-white/80 text-lg">
                  "{product.scent_profile.feels_like}"
                </p>
              </div>
            )}

            <p className="font-body text-white/50 leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Price */}
            <div className="flex items-baseline flex-wrap gap-3 mb-4">
              {product.original_price && product.original_price > product.price && (
                <span className="font-heading text-2xl text-white/40 line-through" data-testid="product-original-price">
                  A${product.original_price.toFixed(2)}
                </span>
              )}
              {product.price > 0 && (
                <span className="font-heading text-4xl text-[#D4AF37]" data-testid="product-price">
                  A${product.price.toFixed(2)}
                </span>
              )}
              {product.discount_pct ? (
                <span className="px-3 py-1 bg-[#D4AF37] text-[#050505] font-heading text-xs tracking-[0.15em]" data-testid="product-discount-badge">
                  {product.discount_pct}% OFF
                </span>
              ) : null}
              <span className="font-body text-white/40 text-sm">{product.size}</span>
            </div>

            {/* Afterpay */}
            <AfterpayBadge amount={product.price} className="mb-8" testId="pdp-afterpay" />

            {product.discontinued && (
              <div className="mb-6 px-4 py-3 border border-red-500/40 bg-red-950/30" data-testid="discontinued-banner">
                <p className="font-heading text-xs tracking-[0.2em] text-red-300">DISCONTINUED — This fragrance is no longer being produced. Once current stock is exhausted, it will not be restocked.</p>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex gap-4 mb-8">
              <div className="flex items-center border border-[#D4AF37]/30">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-4 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus size={16} />
                </button>
                <span className="px-6 font-heading text-white" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-4 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus size={16} />
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={cartLoading || !product.in_stock || product.discontinued}
                className="flex-1 flex items-center justify-center gap-3 bg-[#D4AF37] text-[#050505] font-heading text-xs tracking-[0.2em] hover:bg-[#E5C158] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="add-to-cart-button"
              >
                <ShoppingBag size={18} />
                {product.discontinued ? 'DISCONTINUED' : (!product.in_stock ? 'SOLD OUT' : (cartLoading ? 'ADDING...' : 'ADD TO BAG'))}
              </button>
            </div>

            {/* Checkout Instantly */}
            <button
              onClick={async () => {
                if (product.discontinued) { toast.error('This item is discontinued'); return; }
                if (!product.in_stock) { toast.error('This item is sold out'); return; }
                try {
                  const res = await axios.post(`${API}/checkout/create`, {
                    items: [{ product_id: product.id, quantity }],
                  });
                  if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                } catch { toast.error('Checkout failed'); }
              }}
              disabled={!product.in_stock || product.discontinued}
              className="w-full py-4 mb-6 border border-[#D4AF37] text-[#D4AF37] font-heading text-xs tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="checkout-instantly-button"
            >
              {product.discontinued ? 'DISCONTINUED' : (product.in_stock ? 'CHECKOUT INSTANTLY' : 'UNAVAILABLE')}
            </button>

            {/* Wishlist & Share */}
            <div className="flex gap-4 mb-12">
              <button className="flex-1 flex items-center justify-center gap-2 py-4 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                <Heart size={18} />
                <span className="font-heading text-xs tracking-[0.15em]">WISHLIST</span>
              </button>
              <button className="flex-1 flex items-center justify-center gap-2 py-4 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                <Share2 size={18} />
                <span className="font-heading text-xs tracking-[0.15em]">SHARE</span>
              </button>
            </div>

            {/* Tabs */}
            <div className="border-t border-[#D4AF37]/20 pt-8">
              <div className="flex gap-8 mb-6">
                {['notes', 'profile'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`font-heading text-xs tracking-[0.2em] pb-2 border-b-2 transition-colors ${
                      activeTab === tab ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-white/50'
                    }`}
                  >
                    {tab === 'notes' ? 'SCENT NOTES' : 'SCENT PROFILE'}
                  </button>
                ))}
              </div>

              {activeTab === 'notes' && product.notes && (
                <div className="space-y-6">
                  {['top', 'heart', 'base'].map((noteType) => (
                    product.notes[noteType] && (
                      <div key={noteType}>
                        <p className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-3">
                          {noteType.toUpperCase()} NOTES
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {product.notes[noteType].map((note) => (
                            <span key={note} className="px-4 py-2 border border-[#D4AF37]/20 font-body text-sm text-white/70">
                              {note}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}

              {activeTab === 'profile' && product.scent_profile && (
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(product.scent_profile).filter(([key]) => key !== 'feels_like').map(([key, value]) => (
                    <div key={key} className="p-4 border border-[#D4AF37]/10 bg-[#0a0a0a]/50">
                      <p className="font-heading text-xs tracking-[0.1em] text-[#D4AF37] mb-1">{key.toUpperCase()}</p>
                      <p className="font-body text-white/70">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
